import React, { useState, useEffect, lazy, Suspense } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Typography,
  AppBar,
  Toolbar,
  MenuItem,
  CircularProgress,
  TextField,
  Select,
} from "@mui/material";
import { isEmpty } from "lodash";
import useStyles from "./style";


const categories = ["F", "G", "R"];
const MAX_RETAILER_LENGTH = 30;

const Home: React.FC = () => {
  const navigate = useNavigate();
  const { classes } = useStyles();
  const [retailerName, setRetailer] = useState<string>("");
  const [category, setCategory] = useState<string>("");
  const [currentPosition, setCurrentPosition] = useState<{
    lat: number;
    long: number;
  }>({ lat: 0, long: 0 });
  const [retailerNameError, setRetailerNameError] = useState<string>("");
  const [categoryError, setCategoryError] = useState<string>("");

  useEffect(() => {
    window.history.pushState(null, "", window.location.pathname);
    window.addEventListener("popstate", onBackButtonEvent);

    return () => {
      window.removeEventListener("popstate", onBackButtonEvent);
    };
  }, []);

  const onBackButtonEvent = (e: PopStateEvent) => {
    e.preventDefault();
    window.history.pushState(null, "", window.location.pathname);
  };

  const capitalizeFirstLetter = (str: string): string => {
    if (isEmpty(str)) return str;
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  const validateRetailerName = (name: string): string => {
    if (isEmpty(name.trim())) {
      return "Error";
    }
    if (name.length > MAX_RETAILER_LENGTH) {
      return `Name must not exceed ${MAX_RETAILER_LENGTH} characters`;
    }
    return "";
  };

  const handleRetailerNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;

    // Prevent starting with space
    if (newValue.startsWith(" ") && isEmpty(retailerName)) {
      return; // Don't update state if trying to start with space
    }

    setRetailer(newValue);
    setRetailerNameError(validateRetailerName(newValue));
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    let hasError = false;

    const retailerError = validateRetailerName(retailerName);
    if (!isEmpty(retailerError)) {
      setRetailerNameError(retailerError);
      hasError = true;
    } else {
      setRetailerNameError("");
    }

    if (isEmpty(category)) {
      setCategoryError("Error");
      hasError = true;
    } else {
      setCategoryError("");
    }

    if (hasError) return;

    const capitalizedRetailerName = capitalizeFirstLetter(retailerName);
    localStorage.setItem("selectedRetailer", capitalizedRetailerName);
    localStorage.setItem("selectedCategory", category);

    navigate("/records", {
      state: {
        selectedRetailer: capitalizedRetailerName,
        selectedCategory: category,
      },
    });
  };

  useEffect(() => {
    const geoSuccess = (position: GeolocationPosition) => {
      const { latitude, longitude, accuracy } = position.coords;

      if (accuracy < 100) {
        setCurrentPosition({ lat: latitude, long: longitude });
      } else {
        retryGeolocation();
      }
    };

    const geoError = (error: GeolocationPositionError) => {
      console.error("Geolocation error:", error);
    };

    const retryGeolocation = () => {
      if ("geolocation" in navigator) {
        navigator.geolocation.getCurrentPosition(geoSuccess, geoError, {
          enableHighAccuracy: true,
        });
      }
    };

    retryGeolocation();
  }, []);

  return (
    <>
      <AppBar position="static" className={classes.appBar}>
        <Toolbar>
          <Typography
            variant="h6"
            component="div"
            className={classes.appBarTitle}
          >
            News
          </Typography>
        </Toolbar>
      </AppBar>
      <Box className={classes.mainContainer}>
        <Box className={classes.formContainer}>
          <Suspense
            fallback={
              <Box className={classes.loadingContainer}>
                <CircularProgress />
              </Box>
            }
          >
            <TextField
              name="retailerName"
              label="Enter Name"
              size="medium"
              placeholder="Name"
              fullWidth
              value={retailerName}
              onChange={handleRetailerNameChange}
              className={classes.textField}
              error={!isEmpty(retailerNameError)}
              helperText={retailerNameError}
              inputProps={{
                maxLength: MAX_RETAILER_LENGTH,
              }}
            />
          </Suspense>
          <Box className={classes.selectContainer}>
            <Suspense
              fallback={
                <Box className={classes.loadingContainer}>
                  <CircularProgress />
                </Box>
              }
            >
              <Select
                value={category}
                onChange={(e) => {
                  setCategory(e.target.value as string);
                  setCategoryError("");
                }}
                error={!isEmpty(categoryError)}
                displayEmpty
                inputProps={{ "aria-label": "Without label" }}
                fullWidth
                className={classes.select}
              >
                <MenuItem value="">Select News</MenuItem>
                {categories.map((cat) => (
                  <MenuItem key={cat} value={cat}>
                    {cat}
                  </MenuItem>
                ))}
              </Select>
            </Suspense>
            {!isEmpty(categoryError) && (
              <Typography
                color="error"
                variant="body2"
                className={classes.errorText}
              >
                {categoryError}
              </Typography>
            )}
          </Box>

          <Button
            variant="contained"
            onClick={handleSubmit}
            color="primary"
            className={classes.button}
          >
            Enter
          </Button>
        </Box>
      </Box>
    </>
  );
};

export default Home;
